import { put, call, select } from 'redux-saga/effects';
import { dashboardActions } from '../redux/dashboardSlice';
import {
    fetchDashboardAssignments,
    fetchDashboardNotifications,
    fetchDashboardStats,
    updateNotificationsRead,
    updateNotificationsReadAll,
    updateDashboardAssignment,
    createDashboardAssignment,
    deleteDashboardAssignment,
    fetchDashboardTrackableStatistics,
} from '../../apis/dashboard';
import {
    formatStatsObject,
    formatNotificationsObject,
    formatAssignmentsObject,
    formatAssignmentObject,
    formatAssignmentPayload,
} from './manager';
import {
    ICreateAssignmentAction,
    IDeleteAssignmentAction,
    IUpdateAssignmentAction,
    IUpdateNotificationsReadAction,
} from '../redux/interface';
import { PayloadAction } from '@reduxjs/toolkit';
import { dashboardSelectors } from '../redux/selectors';
import { IClientAssignment, IClientNotification } from '../../../common';
export function* fetchStatsSaga() {
    try {
        const { response, error } = yield call(fetchDashboardStats);
        if (response?.status === 200) {
            yield put(
                dashboardActions.setStats({
                    stats: formatStatsObject(response.data),
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.fetchStatsDone());
    }
}

export function* fetchNotificationsSaga() {
    try {
        const { response, error } = yield call(fetchDashboardNotifications);
        if (response?.status === 200) {
            yield put(
                dashboardActions.setNotifications({
                    notifications: formatNotificationsObject(
                        response.data.results
                    ),
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.fetchNotificationsDone());
    }
}

export function* updateNotificationsReadSaga(
    action: PayloadAction<IUpdateNotificationsReadAction>
) {
    try {
        const { id, readAll } = action.payload;
        if (readAll) {
            const { response, error } = yield call(updateNotificationsReadAll);
            if (response?.status === 200) {
                yield put(
                    dashboardActions.setNotifications({ notifications: [] })
                );
            } else {
                console.log(error);
            }
        } else {
            const { response, error } = yield call(
                updateNotificationsRead,
                id ?? ''
            );
            if (response?.status === 200) {
                const notifications: Array<IClientNotification> = yield select(
                    dashboardSelectors.getNotifications
                );
                const updatedNotifications = notifications.filter(
                    (notification) => notification.id !== id
                );
                yield put(
                    dashboardActions.setNotifications({
                        notifications: updatedNotifications,
                    })
                );
            } else {
                console.log(error);
            }
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.updateNotificationsReadDone());
    }
}

export function* fetchAssignmentsSaga() {
    try {
        const { response, error } = yield call(fetchDashboardAssignments);
        if (response?.status === 200) {
            yield put(
                dashboardActions.setAssignments({
                    assignments: formatAssignmentsObject(
                        response.data.incompleted_assignments
                    ),
                })
            );
            yield put(
                dashboardActions.setCompletedAssignments({
                    completedAssignments: formatAssignmentsObject(
                        response.data.completed_assignments
                    ),
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.fetchAssignmentsDone());
    }
}

export function* updateAssignmentSaga(
    action: PayloadAction<IUpdateAssignmentAction>
) {
    try {
        const { assignment, isMarkAsComplete } = action.payload;
        const { response, error } = yield call(
            updateDashboardAssignment,
            assignment.id,
            formatAssignmentPayload(assignment)
        );
        if (response?.status === 200) {
            let updatedAssignments: Array<IClientAssignment> = [];
            const assignments: Array<IClientAssignment> = yield select(
                dashboardSelectors.getAssignments
            );
            // Mark as complete logic
            if (isMarkAsComplete && !response.data.is_complete) {
                const completedAssignments: Array<IClientAssignment> =
                    yield select(dashboardSelectors.getCompletedAssignments);
                const isInAssignmentsList = assignments.find(
                    (assignment) => assignment.id === response.data.id
                );
                if (!isInAssignmentsList) {
                    updatedAssignments = [
                        formatAssignmentObject(response.data),
                        ...assignments,
                    ];
                } else {
                    updatedAssignments = assignments.map((assignment) => {
                        if (assignment.id === response.data.id) {
                            return {
                                ...assignment,
                                isCompleted: false,
                            };
                        }
                        return assignment;
                    });
                }
                const updatedCompletedAssignments = completedAssignments.filter(
                    (assignment) => assignment.id !== response.data.id
                );
                yield put(
                    dashboardActions.setCompletedAssignments({
                        completedAssignments: updatedCompletedAssignments,
                    })
                );
                // Update task without completing it Logic
            } else {
                updatedAssignments = assignments.map((assignment) => {
                    if (assignment.id === response.data.id) {
                        return formatAssignmentObject(response.data);
                    }
                    return assignment;
                });
            }
            yield put(
                dashboardActions.setAssignments({
                    assignments: updatedAssignments,
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    }
}
export function* createAssignmentSaga(
    action: PayloadAction<ICreateAssignmentAction>
) {
    try {
        const { assignment } = action.payload;
        const { response, error } = yield call(
            createDashboardAssignment,
            formatAssignmentPayload(assignment)
        );
        if (response?.status === 201) {
            const assignments: Array<IClientAssignment> = yield select(
                dashboardSelectors.getAssignments
            );
            const updatedAssignments = [
                ...assignments,
                formatAssignmentObject(response.data),
            ];
            yield put(
                dashboardActions.setAssignments({
                    assignments: updatedAssignments,
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.createAssignmentDone());
    }
}

export function* deleteAssignmentSaga(
    action: PayloadAction<IDeleteAssignmentAction>
) {
    try {
        const { id } = action.payload;
        const { response, error } = yield call(deleteDashboardAssignment, id);
        if (response?.status === 204) {
            const assignments: Array<IClientAssignment> = yield select(
                dashboardSelectors.getAssignments
            );
            const updatedAssignments = assignments.filter(
                (assignment) => assignment.id !== id
            );
            yield put(
                dashboardActions.setAssignments({
                    assignments: updatedAssignments,
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.deleteAssignmentDone());
    }
}

export function* fetchTrackableStatisticsSaga() {
    try {
        const { response, error } = yield call(
            fetchDashboardTrackableStatistics
        );
        if (response?.status === 200) {
            yield put(
                dashboardActions.setTrackableStatistics({
                    trackableStatistics: response.data,
                })
            );
        } else {
            console.log(error);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(dashboardActions.fetchTrackableStatisticsDone());
    }
}
